import { useCookie, useState } from '#app';

export type CookieConsentCategory = 'necessary' | 'analytics';

export interface CookieConsentState {
  analytics: boolean;
  decided: boolean;
}

const COOKIE_NAME = 'cookie_consent';
const COOKIE_MAX_AGE = 365 * 24 * 60 * 60; // 1 year

export function useCookieConsent() {
  const consentCookie = useCookie<CookieConsentState | null>(COOKIE_NAME, {
    maxAge: COOKIE_MAX_AGE,
    path: '/',
    sameSite: 'lax',
  });

  const showBanner = useState<boolean>('cookie-consent-banner', () => {
    return !consentCookie.value?.decided;
  });

  const consentState = useState<CookieConsentState>('cookie-consent-state', () => {
    return consentCookie.value ?? {
      analytics: false,
      decided: false,
    };
  });

  const isAnalyticsAllowed = computed(() => consentState.value.analytics);

  function acceptAll() {
    consentState.value = {
      analytics: true,
      decided: true,
    };
    consentCookie.value = consentState.value;
    showBanner.value = false;
    applyConsent();
  }

  function rejectAll() {
    consentState.value = {
      analytics: false,
      decided: true,
    };
    consentCookie.value = consentState.value;
    showBanner.value = false;
    applyConsent();
  }

  function applyConsent() {
    if (!import.meta.client) {
      return;
    }

    // Google Analytics consent mode
    if (window.gtag) {
      window.gtag('consent', 'update', {
        analytics_storage: consentState.value.analytics ? 'granted' : 'denied',
      });
    }

    // Matomo consent
    window._paq = window._paq || [];
    if (consentState.value.analytics) {
      window._paq.push(['setConsentGiven']);
    } else {
      window._paq.push(['forgetConsentGiven']);
    }
  }

  // Apply saved consent on load
  if (import.meta.client && consentState.value.decided) {
    applyConsent();
  }

  return {
    showBanner,
    consentState,
    isAnalyticsAllowed,
    acceptAll,
    rejectAll,
    applyConsent,
  };
}
